import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PapiUser, PapiDeviation } from '@wix/da-papi-types';
import AddIcon from '@wix/da-ds/pkg/Icons/24x24/Add';
import BrandedButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/BrandedButtonWithContextMenu';
import MediaSelectorWithGroupGallection from '@wix/da-shared-react/pkg/MediaPopup/Groups/MediaSelectorWithGroupGallection';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { GroupGallectionType } from '@wix/da-shared-react/pkg/GroupGallectionSelector/redux/types';
import { showSubmitModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { getSubmitUrlForProfileOwner } from '../../../../../../../utils/urls';

export interface Props {
  profileOwnerUser: PapiUser;
  contributeDeviation: (
    deviation: PapiDeviation,
    group: PapiUser,
    type: GroupGallectionType,
    folderId?: number
  ) => void;
  canContributeDeviations: boolean;
  canRecommendDeviations: boolean;
  canSubmitJournal: boolean;
}

const MEDIA_SOURCE_TYPES_PERSONAL = [
  MediaSourceType.GALLERY,
  MediaSourceType.POSTS,
];
const MEDIA_SOURCE_TYPES_DEVIANTART = [
  MediaSourceType.COLLECTION,
  MediaSourceType.BROWSE,
];

const SubmitButton: React.FC<Props> = ({
  profileOwnerUser,
  contributeDeviation,
  canContributeDeviations,
  canRecommendDeviations,
  canSubmitJournal,
}) => {
  const { t } = useTranslation();
  const [showPersonalDeviations, setShowPersonalDeviations] = useState(false);
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const dispatch = useDispatch();

  if (!canContributeDeviations) {
    return null;
  }

  const openMediaSelector = (ownDeviationsOnly = true) => {
    setShowPersonalDeviations(ownDeviationsOnly);
    setIsMediaSelectorOpen(true);
  };

  const openMediaSelectorPersonal = () => openMediaSelector(true);
  const openMediaSelectorDeviantArt = () => openMediaSelector(false);

  const closeMediaSelector = () => {
    setIsMediaSelectorOpen(false);
  };

  const redirectToSubmitPage = () => {
    window.location.replace(getSubmitUrlForProfileOwner(profileOwnerUser));
  };

  const submitJournal = () => {
    dispatch(
      showSubmitModal(ModalType.JOURNAL, {
        params: {
          refreshAfterSubmit: true,
          groupDestination: profileOwnerUser,
        },
      })
    );
  };
  const submitStatusUpdate = () => {
    dispatch(
      showSubmitModal(ModalType.STATUS_UPDATE, {
        params: { refreshAfterSubmit: true },
      })
    );
  };

  const items = [
    canContributeDeviations && {
      label: t('groups.submit.contributeNewDeviation'),
      onClick: redirectToSubmitPage,
    },
    canContributeDeviations && {
      label: t('groups.submit.contributeExistingDeviation'),
      onClick: openMediaSelectorPersonal,
    },
    canRecommendDeviations && {
      label: t('groups.submit.recommendExistingDeviation'),
      onClick: openMediaSelectorDeviantArt,
    },
    canSubmitJournal && {
      label: t('groups.submit.writePost'),
      onClick: profileOwnerUser.isGroup ? submitJournal : submitStatusUpdate,
    },
  ].filter(x => x);

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <BrandedButtonWithContextMenu
        icon={AddIcon}
        items={items}
        size="large"
        variant="brand-green"
      >
        {t('common.submit')}
      </BrandedButtonWithContextMenu>
      {isMediaSelectorOpen && (
        <MediaSelectorWithGroupGallection
          group={profileOwnerUser}
          onClose={closeMediaSelector}
          mediaSourceTypes={
            showPersonalDeviations
              ? MEDIA_SOURCE_TYPES_PERSONAL
              : MEDIA_SOURCE_TYPES_DEVIANTART
          }
          initialMediaSource={
            showPersonalDeviations
              ? MEDIA_SOURCE_TYPES_PERSONAL[0]
              : MEDIA_SOURCE_TYPES_DEVIANTART[0]
          }
          onSelect={(...args) => {
            contributeDeviation(...args);
            setIsMediaSelectorOpen(false);
          }}
        />
      )}
    </>
  );
};
SubmitButton.displayName = 'SubmitButton';

export default SubmitButton;
